import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";
import * as XLSX from "xlsx";

import { IDynamicCsvColumns, useCsvDefDynamic } from "@ryde/controls";
import {
  CSV_ESCAPABLE_CHARS_REGEX,
  errors,
  GENERIC_EXPORT_BATCH_SIZE,
  GenericExportFormat,
  gridControlsSF,
  Log,
  PLURAL_KEY,
  tableColumnsOrderStateAF,
  tableColumnsVisibilityAF,
  type IExtendableDto,
  type ObjectLiteral,
  type RydeUiUnit,
} from "@ryde/shared";

import { getActiveItems, getInactiveItems } from "../../../right-drawers";
import { applyXlsxCellTransformers } from "./generic-export-xlsx.transformers";
import {
  fetchMissingEntityDtosByIds,
  mergeSelectedAndFetchedRows,
  sanitizeSheetName,
} from "./generic-export.helpers";

interface IUseGenericExportParams {
  entity: RydeUiUnit;
  selectedRowsData: IExtendableDto[];
  selectedIds?: number[];
}

interface IUseGenericExportResult {
  rowsData: IExtendableDto[];
  isFetchingRows: boolean;
  csvData: ObjectLiteral<unknown>[];
  loading: boolean;
  format: GenericExportFormat;
  setFormat: (format: GenericExportFormat) => void;
  wrapperRef: React.MutableRefObject<HTMLDivElement | null>;
  currentEntityName: string;
  handleExport: (isDownloadAllFields: boolean) => Promise<void>;
}

export const useGenericExport = ({
  entity,
  selectedRowsData,
  selectedIds,
}: IUseGenericExportParams): IUseGenericExportResult => {
  const [rowsData, setRowsData] = useState<IExtendableDto[]>(selectedRowsData);
  const [isFetchingRows, setIsFetchingRows] = useState(false);
  const [csvData, setCsvData] = useState<ObjectLiteral<unknown>[]>([]);
  const [loading, setLoading] = useState(false);
  const [format, setFormat] = useState<GenericExportFormat>(
    GenericExportFormat.Csv,
  );
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const gridControls = useRecoilValue(gridControlsSF(entity));
  const { dynamicCsvColumns } = useCsvDefDynamic({ gridControls, entity });
  const columnOrder = useRecoilValue(tableColumnsOrderStateAF(entity));
  const columnVisibility = useRecoilValue(tableColumnsVisibilityAF(entity));
  const { t } = useTranslation();

  const currentEntityName = t(`${entity}.${PLURAL_KEY}`);
  const idHeader = t(`${entity}.gridLabel.id`);

  const targetIds = useMemo(() => {
    if (selectedIds && selectedIds.length > 0) {
      return selectedIds;
    }

    return selectedRowsData.map(row => row.id);
  }, [selectedIds, selectedRowsData]);

  useEffect(() => {
    const loadedMap = new Map(selectedRowsData.map(row => [row.id, row]));
    const missingIds = targetIds.filter(id => !loadedMap.has(id));

    if (!missingIds.length) {
      setRowsData(selectedRowsData);
      setIsFetchingRows(false);
      return;
    }

    let isCancelled = false;
    setIsFetchingRows(true);

    void (async () => {
      try {
        const fetchedResult = await fetchMissingEntityDtosByIds({
          entity,
          ids: missingIds,
          t,
        });

        if (isCancelled) {
          return;
        }

        if (!fetchedResult.success || !fetchedResult.data) {
          setRowsData(selectedRowsData);
          return;
        }

        const merged = mergeSelectedAndFetchedRows({
          targetIds,
          existingRows: selectedRowsData,
          fetchedRows: fetchedResult.data,
        });

        setRowsData(merged);
      } catch (error) {
        Log.errorAndToast(t(errors.common.unknownError), error);
        if (!isCancelled) {
          setRowsData(selectedRowsData);
        }
      } finally {
        if (!isCancelled) {
          setIsFetchingRows(false);
        }
      }
    })();

    return () => {
      isCancelled = true;
    };
  }, [entity, selectedRowsData, t, targetIds]);

  const { activeColumns, inactiveColumns } = useMemo(() => {
    return {
      activeColumns: getActiveItems(
        dynamicCsvColumns,
        columnVisibility,
        columnOrder,
      ),
      inactiveColumns: getInactiveItems(dynamicCsvColumns, columnVisibility),
    };
  }, [columnOrder, columnVisibility, dynamicCsvColumns]);

  const collectRows = useCallback(
    async (
      finalColumns: IDynamicCsvColumns[],
      pickCell: (
        column: IDynamicCsvColumns,
      ) => IDynamicCsvColumns["getCellValue"],
      escapeStrings: boolean,
    ): Promise<ObjectLiteral<unknown>[]> => {
      const data: ObjectLiteral<unknown>[] = [];

      const processBatch = async (startIndex: number): Promise<void> => {
        const endIndex = Math.min(
          startIndex + GENERIC_EXPORT_BATCH_SIZE,
          rowsData.length,
        );

        for (let i = startIndex; i < endIndex; i++) {
          const row = rowsData[i];
          const item: ObjectLiteral<unknown> = { [idHeader]: row.id };

          for (const column of finalColumns) {
            let cellValue = await pickCell(column)(row);

            if (
              escapeStrings &&
              typeof cellValue === "string" &&
              CSV_ESCAPABLE_CHARS_REGEX.test(cellValue)
            ) {
              cellValue = cellValue.replace(/"/g, '""');
            }

            item[column.header] = cellValue;
          }

          data.push(item);
        }

        if (endIndex < rowsData.length) {
          // Yield to the browser between batches.
          await new Promise<void>(resolve => setTimeout(resolve, 0));
          await processBatch(endIndex);
        }
      };

      await processBatch(0);
      return data;
    },
    [idHeader, rowsData],
  );

  const triggerCsvDownload = useCallback(
    (rows: ObjectLiteral<unknown>[]): void => {
      setCsvData(rows);
      // Wait one tick for CSVLink to receive the new data, then click its anchor.
      setTimeout(() => {
        const anchor = wrapperRef.current?.querySelector("a");
        anchor?.click();
      }, 0);
    },
    [],
  );

  const triggerXlsxDownload = useCallback(
    async (
      rows: ObjectLiteral<unknown>[],
      headers: string[],
      columns: IDynamicCsvColumns[],
    ): Promise<void> => {
      const worksheet = XLSX.utils.json_to_sheet(rows, { header: headers });
      applyXlsxCellTransformers(worksheet, columns, rows.length);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        sanitizeSheetName(currentEntityName),
      );
      XLSX.writeFile(workbook, `${currentEntityName}.xlsx`);
    },
    [currentEntityName],
  );

  const handleExport = useCallback(
    async (isDownloadAllFields: boolean): Promise<void> => {
      setLoading(true);

      const finalColumns = [
        ...activeColumns,
        ...(isDownloadAllFields ? inactiveColumns : []),
      ] as IDynamicCsvColumns[];

      const rows = await collectRows(
        finalColumns,
        column => column.getCellValue,
        format === GenericExportFormat.Csv,
      );

      if (format === GenericExportFormat.Csv) {
        triggerCsvDownload(rows);
      } else {
        const headers = [idHeader, ...finalColumns.map(c => c.header)];
        await triggerXlsxDownload(rows, headers, finalColumns);
      }

      setLoading(false);
    },
    [
      activeColumns,
      collectRows,
      format,
      idHeader,
      inactiveColumns,
      triggerCsvDownload,
      triggerXlsxDownload,
    ],
  );

  return {
    rowsData,
    isFetchingRows,
    csvData,
    loading,
    format,
    setFormat,
    wrapperRef,
    currentEntityName,
    handleExport,
  };
};
